<template>
    <div
        v-longpress="{ handler: onLongPress, cancelOnTouchMove: true, longPressDuration: 500 }"
        v-press="onPress"
        class="label"
        :class="[$mq, { 'is-selected': selected, 'no-images': !showImages }]"
        @contextmenu.prevent
    >
        <span
            class="color"
            v-if="showCategoryColor"
            v-bind:style="{
                backgroundColor: categoryColor
            }"
        ></span>
        <i v-if="showTimerIndicator" class="i-andy-menu-timer timer-indicator" aria-hidden="true"></i>
        <div v-if="showImages && !isApp" class="image">
            <img :src="imageSrc" :alt="label.name || ''" />
        </div>
        <div
            v-else-if="showImages"
            class="imageapp"
            :class="{ appempty: !label.local_image && !label.image }"
            :style="{
                backgroundImage: 'url(' + (label.local_image ? label.local_image : label.image) + ')'
            }"
        ></div>
        <div class="label-text">
            <span class="label-text__name">{{ label.name }}</span>
        </div>
        <div class="meta">
            <div class="column" v-for="state in states" :key="state.id || state.status" :class="['status-' + state.status]"></div>
        </div>

        <div v-if="selected" class="select-product" aria-hidden="true">
            <div class="corner-flag">
                <span class="checkmark"></span>
            </div>
            <div class="border-white"></div>
        </div>
    </div>
</template>

<script>
/**
 * Tarjeta de producto del listado timerproduct con la misma interacción
 * de selección múltiple que timer.vue (v-longpress + v-press).
 */
export default {
    name: 'ProductLabelCard',
    props: {
        label: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            default: 0
        },
        showCategoryColor: {
            type: Boolean,
            default: false
        },
        categoryColor: {
            type: String,
            default: 'transparent'
        },
        states: {
            type: Array,
            default() {
                return []
            }
        },
        selected: {
            type: Boolean,
            default: false
        },
        isMultiSelectMode: {
            type: Boolean,
            default: false
        },
        hasTimer: {
            type: Boolean,
            default: false
        },
        /**
         * BUILD-7687: si es false, oculta la miniatura.
         * Nombre y barra de estados se mantienen.
         */
        showImages: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            /**
             * Tras un long-press, el navegador (sobre todo touch) dispara un click
             * sintético que volvería a hacer toggle y apagaría la selección al instante.
             */
            suppressNextPress: false,
            suppressPressTimer: null
        }
    },
    computed: {
        appURL() {
            return process.env.VUE_APP_APP_URL
        },
        isApp() {
            return process.env.VUE_APP_IS_APP == 'TRUE'
        },
        imageSrc() {
            return this.label.image ? this.label.image : this.appURL + 'img/offline/no_icon_light.svg'
        },
        showTimerIndicator() {
            return this.hasTimer && !this.selected
        }
    },
    beforeDestroy() {
        if (this.suppressPressTimer) {
            clearTimeout(this.suppressPressTimer)
        }
    },
    methods: {
        onPress() {
            if (this.suppressNextPress) {
                this.suppressNextPress = false
                return
            }

            if (this.isMultiSelectMode) {
                this.$emit('toggle-select', this.label)
                return
            }
            this.$emit('select', this.label)
        },
        onLongPress() {
            this.suppressNextPress = true
            if (this.suppressPressTimer) {
                clearTimeout(this.suppressPressTimer)
            }
            // Ventana para absorber el click sintético posterior al long-press
            this.suppressPressTimer = setTimeout(() => {
                this.suppressNextPress = false
                this.suppressPressTimer = null
            }, 600)

            this.$emit('long-press', this.label)
        }
    }
}
</script>

<style lang="scss" scoped>
.label {
    @include border-radius(5px);
    @include display-flex();
    @include flex-direction(column);
    background-color: #fff;
    width: 100%;
    height: 100%;
    padding: 5px;
    position: relative;
    cursor: pointer;
    overflow: hidden;
    touch-action: pan-y;
    user-select: none;
    -webkit-touch-callout: none;
    -webkit-user-select: none;

    .color {
        @include border-radius(10px);
        width: 18px;
        height: 18px;
        position: absolute;
        top: 5px;
        left: 5px;
    }

    .timer-indicator {
        position: absolute;
        top: 5px;
        right: 5px;
        font-size: 20px;
        line-height: 1;
        color: $color-neutral-400;
        z-index: 2;
        pointer-events: none;
    }

    .image {
        display: block;
        margin: 10px auto;
        text-align: center;
        height: 60%;
        margin-top: 3px;
        padding: 15px;
        min-height: 0;
        overflow: hidden;
        flex-shrink: 1;

        img {
            height: 100%;
            object-fit: contain;
            pointer-events: none;
        }
    }

    .label-text {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        width: 100%;
        height: 30%;
        max-height: 30%;
        min-height: 0;
        flex-shrink: 0;
        overflow: hidden;
        box-sizing: border-box;

        &__name {
            @include font-size(sm);
            @include text-ellipsis($line: 2);
            font-family: $text-bold;
            text-align: center;
            color: $main-s90;
            // line-height < font-size recortaba glifos y dejaba restos de la 3ª línea
            line-height: 1.2;
            max-height: 2.4em;
            max-width: 100%;
            word-break: break-word;
            overflow-wrap: break-word;
            text-overflow: ellipsis;
        }
    }

    .imageapp {
        @include background($image: img('offline/no_icon_light.svg'));
        display: block;
        margin: 10px auto;
        text-align: center;
        height: 60%;
        margin-top: 3px;
        padding: 15px;
        width: 60%;
        pointer-events: none;
        min-height: 0;
        overflow: hidden;
        flex-shrink: 1;

        &.appempty {
            @include background($image: img('offline/no_icon_light.svg') !important);
        }
    }

    &.no-images {
        padding: 8px 6px 5px;

        .label-text {
            flex: 1 1 auto;
            height: auto;
            max-height: none;
        }
    }

    .meta {
        width: 100%;
        height: 7px;
        min-height: 7px;
        flex-shrink: 0;
        display: flex;
        border-radius: 1px;
        margin-top: auto;

        .column {
            content: ' ';
            flex: 1;
            margin: 0 2px;

            &.status--1 {
                background-color: $color-neutral-300 !important;
            }
            &.status-0 {
                background-color: $color-error-500 !important;
            }
            &.status-1 {
                background-color: $color-secondary-500 !important;
            }
            &.status-2 {
                background-color: $done !important;
            }
            &.status-3 {
                background-color: $color-warning-500 !important;
            }
        }
    }

    .select-product {
        background-color: rgba(238, 229, 229, 0.3);
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
        border: 4px solid $color-info-500;
        pointer-events: none;

        .border-white {
            width: 100%;
            height: 100%;
            border: 4px solid $color-white;
        }
    }

    .corner-flag {
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-top: 50px solid $color-info-500;
        border-left: 50px solid transparent;
    }

    .checkmark {
        background-image: img('checked_timer.svg');
        background-size: contain;
        background-repeat: no-repeat;
        width: 24px;
        height: 24px;
        position: absolute;
        top: -45px;
        right: 0;
        font-size: 24px;
    }

    &.portrait {
        .label-text__name {
            @include font-size(s);
            line-height: 1.2;
        }
    }
}
</style>
